import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useParams, Navigate } from "react-router-dom";
import { PageMeta } from "@/components/common/PageMeta";
import { Badge } from "@/components/common/Badge";
import { SlideContainer, slideComponents } from "@/components/investors/SlideContainer";
import { SlideGallery } from "@/components/investors/SlideGallery";
import { EditorSidebar } from "@/components/investors/EditorSidebar";
import { IRSubNav } from "@/components/ir/IRSubNav";
import { DocumentWatermark } from "@/components/ir/DocumentWatermark";
import { useTheme } from "@/hooks/useTheme";
import { INVESTOR_SLIDES, INVESTOR_V2_SLIDES, INVESTOR_V3_SLIDES, LEGAL_SLIDES, INTRO_SLIDES } from "@/lib/constants";
import type { SlideMode, SlideEntry } from "@/lib/constants";
import { getDocument } from "@/lib/documents";
import { legalSlideComponents } from "@/components/legal-deck/LegalSlideContainer";
import { introSlideComponents } from "@/components/intro-deck/IntroSlideContainer";
import { investorV2SlideComponents } from "@/components/investor-deck-v2/InvestorV2SlideContainer";
import { investorV3SlideComponents } from "@/components/investor-deck-v3/InvestorV3SlideContainer";

const MD_BREAKPOINT = 768;

// Map document IDs to their slide data and components
const DECK_MAP: Record<string, {
  slides: readonly SlideEntry[];
  components: React.ComponentType<{ isActive: boolean }>[];
  meta: { id: string; name: string; date: string };
}> = {
  "CX-001-A": {
    slides: INTRO_SLIDES,
    components: introSlideComponents,
    meta: { id: "CX-001-A", name: "COSX_COMPANY_INTRO", date: "2026.03.26" },
  },
  "CX-002-B": {
    slides: INVESTOR_V2_SLIDES,
    components: investorV2SlideComponents,
    meta: { id: "CX-002-B", name: "COSX_PITCH_DECK_V2", date: "2026.03.30" },
  },
  "CX-002-C": {
    slides: INVESTOR_V3_SLIDES,
    components: investorV3SlideComponents,
    meta: { id: "CX-002-C", name: "COSX_PITCH_DECK_V3", date: "2026.04.28" },
  },
  "CX-002-A": {
    slides: INVESTOR_SLIDES,
    components: slideComponents,
    meta: { id: "CX-002-A", name: "COSX_PITCH_DECK", date: "2026.03.15" },
  },
  "CX-003-A": {
    slides: LEGAL_SLIDES,
    components: legalSlideComponents,
    meta: { id: "CX-003-A", name: "COSX_LEGAL_DECK", date: "2026.03.26" },
  },
};

function getInitialMode(): SlideMode {
  if (typeof window === "undefined") return "normal";
  const params = new URLSearchParams(window.location.search);
  if (params.get("mode") === "export") return "export";
  return "normal";
}

export function DocumentViewerPage() {
  const { docId } = useParams<{ docId: string }>();
  const doc = docId ? getDocument(docId) : undefined;

  const { theme } = useTheme();
  const isDark = theme === "dark";
  const canvasBg = isDark ? "#0f0f0f" : "#f4f4f5";
  const dotColor = isDark ? "#1f1f1f" : "#e4e4e7";
  const sectionBg = isDark ? "rgba(10,10,10,0.75)" : "rgba(255,255,255,0.75)";

  const [mode, setMode] = useState<SlideMode>(getInitialMode);
  const isExport = mode === "export";
  const [activeSlide, setActiveSlide] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => typeof window !== "undefined" && window.innerWidth < MD_BREAKPOINT
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MD_BREAKPOINT - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setSidebarCollapsed(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const slideIndexRef = useRef(0);
  const scrollToSlideRef = useRef<((index: number) => void) | null>(null);

  const enterPresentation = useCallback((slideIndex: number) => {
    slideIndexRef.current = slideIndex;
    setMode("presentation");
  }, []);

  const deck = docId ? DECK_MAP[docId] : undefined;

  const exitPresentation = useCallback((slideIndex: number) => {
    setMode("normal");
    requestAnimationFrame(() => {
      const slides = deck?.slides ?? INVESTOR_SLIDES;
      const el = document.getElementById(`slide-${slides[slideIndex]?.id}`);
      el?.scrollIntoView({ behavior: "instant", block: "center" });
    });
  }, [deck]);

  const handleSlideClick = useCallback((index: number) => {
    scrollToSlideRef.current?.(index);
  }, []);

  const printStyle = useMemo(() => {
    if (!isExport) return null;
    return (
      <style dangerouslySetInnerHTML={{ __html: `
        @page { size: 1024px 576px; margin: 0; }
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      `}} />
    );
  }, [isExport]);

  if (!doc) {
    return <Navigate to="/ir/dataroom/docs" replace />;
  }

  // Placeholder for non-slides viewer types
  if (doc.viewerType !== "slides") {
    return (
      <>
        <PageMeta
          title={`${doc.title} | COSX Dataroom`}
          description={doc.description}
          path={`/ir/dataroom/docs/${doc.id}`}
          noindex
        />
        <main className="relative z-10 min-h-screen pt-[var(--topnav-height)]">
          <div
            className="backdrop-blur-[2px] min-h-[calc(100vh-var(--topnav-height))]"
            style={{ background: sectionBg }}
          >
            <div className="max-w-6xl mx-auto px-8 pt-16 pb-20">
              <IRSubNav />
              <Badge className="mb-6">{doc.id}</Badge>
              <h1 className="text-3xl md:text-5xl font-light tracking-tight mb-4 font-[family-name:var(--font-display)]">
                {doc.title}
              </h1>
              <div className="mt-10 p-16 border border-dashed border-[var(--color-border)] flex items-center justify-center">
                <span className="text-sm text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)]">
                  {doc.viewerType === "pdf" ? "PDF viewer" : "Document viewer"} coming soon
                </span>
              </div>
            </div>
          </div>
        </main>
      </>
    );
  }

  // Slides viewer — uses its own dot-matrix background over the layout bg
  return (
    <div
      className={`relative z-10 min-h-screen${isExport ? "" : " viewer-enter"}`}
      style={isExport ? { background: "transparent" } : {
        background: canvasBg,
        backgroundImage: `radial-gradient(${dotColor} 1px, transparent 1px)`,
        backgroundSize: "20px 20px",
      }}
    >
      {printStyle}
      <DocumentWatermark />
      <PageMeta
        title={`${doc.title} | COSX Dataroom`}
        description={doc.description}
        path={`/ir/dataroom/docs/${doc.id}`}
        noindex
      />
      {mode === "normal" || mode === "export" ? (
        <>
          {!isExport && (
            <EditorSidebar
              activeSlide={activeSlide}
              onSlideClick={handleSlideClick}
              isCollapsed={sidebarCollapsed}
              onToggle={() => setSidebarCollapsed((v) => !v)}
              slides={deck?.slides}
              deckMeta={deck?.meta}
            />
          )}
          <SlideGallery
            onPresent={enterPresentation}
            onActiveSlideChange={setActiveSlide}
            scrollToSlideRef={scrollToSlideRef}
            sidebarCollapsed={sidebarCollapsed}
            mode={mode}
            slides={deck?.slides}
            components={deck?.components}
          />
        </>
      ) : (
        <SlideContainer
          initialSlide={slideIndexRef.current}
          onExit={exitPresentation}
          slides={deck?.slides}
          components={deck?.components}
        />
      )}
    </div>
  );
}
